import { type FC } from 'react';
import { RiArrowDownSLine } from 'react-icons/ri';
import type { StepStatus } from '../VerticalStepForm.model';
import styles from './StepHeader.module.scss';

interface StepHeaderProps {
  stepNumber: number;
  stepId: string;
  title: string;
  status: StepStatus;
  required: boolean;
  isExpanded: boolean;
  onGoToStep: (stepId: string) => void;
}

/** Clickable header row for a step. Shows title, required badge, skipped state, and expand chevron. */
const StepHeader: FC<StepHeaderProps> = ({ stepNumber, stepId, title, status, required, isExpanded, onGoToStep }) => {
  const isClickable = status !== 'pending';

  const handleClick = (): void => {
    if (isClickable) {
      onGoToStep(stepId);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onGoToStep(stepId);
    }
  };

  return (
    <div
      className={`${styles.header} ${styles[status] ?? ''} ${isClickable && !isExpanded ? styles.clickable : ''}`}
      data-test-component="StepHeader"
      data-test-element="container"
      data-testid={`step-header--${stepNumber}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
    >
      <div className={styles.content}>
        <span className={`${styles.title} basier-med`}>{title}</span>
        {required && (
          <span className={styles.requiredBadge} data-testid={`step-header--${stepNumber}__required`}>
            required
          </span>
        )}
        {status === 'skipped' && (
          <span className={styles.skippedBadge} data-testid={`step-header--${stepNumber}__skipped`}>
            Skipped
          </span>
        )}
      </div>

      <div className={styles.actions}>
        {status !== 'pending' && (
          <RiArrowDownSLine size={20} className={`${styles.chevron} ${isExpanded ? styles.chevronOpen : ''}`} />
        )}
      </div>
    </div>
  );
};

export default StepHeader;
